<template>
  <div class="files-workspace">
    <div class="files-shell">
      <header class="files-hero">
        <div>
          <div class="eyebrow">REMOTE FILE DOCK</div>
          <h2>云主机文件管理</h2>
          <p>上传、下载和清理远程工作目录文件，适合投递脚本、素材和运行产物。</p>
        </div>
        <el-button type="primary" size="large" @click="showUploadDialog = true">
          <el-icon class="el-icon--left"><Upload /></el-icon>
          上传文件
        </el-button>
      </header>

      <section class="file-stats">
        <div class="stat-card">
          <span class="stat-value">{{ fileList.length }}</span>
          <span class="stat-label">文件数量</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ totalFileSize }}</span>
          <span class="stat-label">总占用</span>
        </div>
        <div class="stat-card wide">
          <span class="stat-value">{{ latestModifiedLabel }}</span>
          <span class="stat-label">最近更新</span>
        </div>
      </section>

      <el-card class="files-card" shadow="never">
      <div class="files-content">
        <el-table
          :data="fileList"
          v-loading="loading"
          style="width: 100%"
          :empty-text="fileList.length === 0 ? '暂无文件' : ''"
        >
          <el-table-column prop="filename" label="文件名" min-width="200" />
          <el-table-column label="大小" width="120">
            <template #default="{ row }">
              {{ formatFileSize(row.size) }}
            </template>
          </el-table-column>
          <el-table-column label="修改时间" width="180">
            <template #default="{ row }">
              {{ formatTime(row.modified) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template #default="{ row }">
              <el-button
                type="primary"
                size="small"
                link
                @click="downloadFile(row.filename)"
              >
                <el-icon><Download /></el-icon> 下载
              </el-button>
              <el-button
                type="danger"
                size="small"
                link
                @click="confirmDelete(row.filename)"
              >
                <el-icon><Delete /></el-icon> 删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      </el-card>
    </div>

    <!-- 上传对话框 -->
    <el-dialog
      v-model="showUploadDialog"
      title="上传文件到云主机"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-upload
        ref="uploadRef"
        :auto-upload="false"
        :limit="1"
        :on-change="handleFileChange"
        :on-exceed="handleExceed"
        drag
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">拖拽文件到此处或 <em>点击选择</em></div>
        <template #tip>
          <div class="el-upload__tip">单次只能上传一个文件，最大 500MB</div>
        </template>
      </el-upload>

      <template #footer>
        <el-button @click="showUploadDialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="uploading"
          :disabled="!selectedFile"
          @click="handleUpload"
        >
          {{ uploading ? "上传中..." : "确认上传" }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Upload,
  Download,
  Delete,
  UploadFilled,
} from "@element-plus/icons-vue";

const API_BASE = "/api/files";

const fileList = ref([]);
const loading = ref(false);
const showUploadDialog = ref(false);
const uploading = ref(false);
const selectedFile = ref(null);
const uploadRef = ref(null);

const totalFileSize = computed(() => {
  const total = fileList.value.reduce((sum, item) => sum + (item.size || 0), 0);
  return formatFileSize(total);
});

const latestModifiedLabel = computed(() => {
  if (!fileList.value.length) return "暂无";
  const latest = Math.max(...fileList.value.map((item) => item.modified || 0));
  return formatTime(latest);
});

const fetchFileList = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`${API_BASE}/list`);
    fileList.value = res.data.files || [];
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "获取文件列表失败");
  } finally {
    loading.value = false;
  }
};

const handleFileChange = (file) => {
  selectedFile.value = file.raw;
};

const handleExceed = () => {
  ElMessage.warning("单次只能上传一个文件");
};

const handleUpload = async () => {
  if (!selectedFile.value) return;

  uploading.value = true;
  const formData = new FormData();
  formData.append("file", selectedFile.value);

  try {
    const res = await axios.post(`${API_BASE}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    ElMessage.success(res.data.message || "上传成功");
    showUploadDialog.value = false;
    selectedFile.value = null;
    uploadRef.value?.clearFiles();
    fetchFileList();
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "上传失败");
  } finally {
    uploading.value = false;
  }
};

const downloadFile = async (filename) => {
  try {
    const res = await axios.get(`${API_BASE}/download/${filename}`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    ElMessage.success("下载成功");
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "下载失败");
  }
};

const confirmDelete = (filename) => {
  ElMessageBox.confirm(`确定要删除文件 "${filename}" 吗？`, "删除确认", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => deleteFile(filename))
    .catch(() => {});
};

const deleteFile = async (filename) => {
  try {
    await axios.delete(`${API_BASE}/delete/${filename}`);
    ElMessage.success("删除成功");
    fetchFileList();
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "删除失败");
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(2) + " " + sizes[i];
};

const formatTime = (timestamp) => {
  return new Date(timestamp * 1000).toLocaleString("zh-CN", {
    timeZone: "Asia/Shanghai",
  });
};

onMounted(() => {
  fetchFileList();
});
</script>

<style scoped>
.files-workspace {
  --panel-border: #dce6f2;
  --ink: #203040;
  --muted: #6d7b8a;
  padding: 36px 48px 56px;
  height: 100%;
  overflow-y: auto;
  background:
    radial-gradient(circle at 12% 14%, rgba(64, 158, 255, 0.12), transparent 28%),
    linear-gradient(135deg, #f6f9fc 0%, #edf3f8 100%);
}

.files-shell {
  max-width: 1360px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.files-card {
  border-radius: 22px;
  border: 1px solid var(--panel-border);
  box-shadow: 0 18px 42px rgba(55, 84, 112, 0.1);
  overflow: hidden;
}

.files-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 30px 34px;
  border: 1px solid var(--panel-border);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(241, 247, 252, 0.86)),
    repeating-linear-gradient(90deg, transparent 0 23px, rgba(32, 48, 64, 0.035) 24px);
  box-shadow: 0 24px 60px rgba(55, 84, 112, 0.12);
}
.eyebrow {
  color: #409eff;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}
.files-hero h2 {
  margin: 0 0 12px;
  font-size: 34px;
  line-height: 1;
  color: var(--ink);
  letter-spacing: -0.04em;
}
.files-hero p {
  margin: 0;
  color: var(--muted);
  font-size: 15px;
}
.file-stats {
  display: grid;
  grid-template-columns: 180px 180px minmax(280px, 1fr);
  gap: 16px;
}
.stat-card {
  min-height: 98px;
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid var(--panel-border);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 14px 34px rgba(55, 84, 112, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.stat-card.wide {
  background: #203040;
  color: #fff;
}
.stat-value {
  font-size: 26px;
  font-weight: 900;
  line-height: 1.1;
  color: var(--ink);
}
.stat-card.wide .stat-value {
  color: #fff;
  font-size: 20px;
}
.stat-label {
  margin-top: 8px;
  color: var(--muted);
  font-size: 13px;
}
.stat-card.wide .stat-label {
  color: rgba(255, 255, 255, 0.72);
}

.files-content {
  min-height: 460px;
  padding: 8px;
}

:deep(.el-table) {
  --el-table-header-bg-color: #f7fafd;
  --el-table-border-color: #edf2f7;
  border-radius: 16px;
  overflow: hidden;
}

.el-icon--upload {
  font-size: 67px;
  color: #409eff;
  margin-bottom: 16px;
}
@media (max-width: 980px) {
  .files-workspace {
    padding: 24px;
  }
  .files-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .file-stats {
    grid-template-columns: 1fr;
  }
}
</style>
